import { useCallback, useEffect, useState } from 'react';
import { getPublicPricing, PRICING_LOAD_MESSAGE, PricingError } from '../../services/pricing';
import type { PublicPricingResponse } from '../../types/pricing';

export type PublicPricingState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; pricing: PublicPricingResponse };

/**
 * Loads the public price list, with a `retry` for the error state.
 *
 * Each load aborts the one before it, so a slow first response can never land on top of a retry's
 * answer, and an unmount drops whatever is still in flight.
 */
export const usePublicPricing = (): { state: PublicPricingState; retry: () => void } => {
  const [state, setState] = useState<PublicPricingState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    getPublicPricing(controller.signal)
      .then((pricing) => {
        if (!controller.signal.aborted) setState({ status: 'ready', pricing });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: 'error',
          message: error instanceof PricingError ? error.message : PRICING_LOAD_MESSAGE,
        });
      });
    return () => controller.abort();
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { state, retry };
};
